import { lazy, Suspense, useState } from 'react';
import type { EmojiStyle, Theme } from 'emoji-picker-react';
import { Sheet } from '../ui/Sheet';
import { TextField } from '../ui/TextField';
import { Button } from '../ui/Button';
import { isMultiCharSignalIcon } from '../../lib/signalIcon';

// Shared by TemplateManageScreen (editing a template's signal list) and
// SaveAsSignalSheet (turning a chat message into a new signal preset) -
// both want the exact same "full edit" controls for a single
// TemplateSignal's look, not two divergent copies.
export const DEFAULT_SIGNAL_COLOR = '#10B981';
export const DEFAULT_SIGNAL_EMOJI = '✨';

// The emoji dataset is sizeable (~600KB) - lazy-loaded so it's only fetched
// when a signal's icon picker is actually opened, not on every page that
// happens to render one of these controls.
const EmojiPicker = lazy(() => import('emoji-picker-react'));

// Each signal picks its own color (native <input type="color">, which opens
// the OS's real color picker instead of a curated swatch list).
export function SignalColorPicker({ colorHex, onColorChange }: { colorHex: string; onColorChange: (color: string) => void }) {
  return (
    <input
      type="color"
      aria-label="Signal color"
      value={colorHex}
      onChange={(e) => onColorChange(e.target.value)}
      className="w-9 h-9 rounded-lg border border-black/15 dark:border-white/15 bg-transparent p-0.5 cursor-pointer shrink-0"
    />
  );
}

// The signal's icon IS its emoji picker - clicking it opens a real picker:
// the full emoji set as a tappable grid, with a search field to find one by
// description/keyword (e.g. "fire" -> 🔥), rather than relying on the OS's
// own emoji keyboard/shortcut. Rendered as the same tinted-circle medallion
// a sent signal message uses (see MessagesScreen.tsx's SignalMedallion) -
// colorHex is live from the row's own SignalColorPicker, so this doubles
// as a preview of exactly what the signal will look like once sent.
export function SignalEmojiPicker({
  emoji,
  colorHex,
  onEmojiChange,
}: {
  emoji: string;
  colorHex: string;
  onEmojiChange: (emoji: string) => void;
}) {
  const [open, setOpen] = useState(false);
  // Seeded from the current icon whenever the sheet opens, so re-opening
  // it to tweak a signal already using a 2-char label starts from that
  // label instead of blank - see the onClick below.
  const [customText, setCustomText] = useState('');
  // Matches the class StoreContext's theme effect actually applies to
  // <html> (system preference already resolved into it), so the picker's
  // own dark mode stays in sync with whatever the app is currently showing,
  // not just the OS's raw prefers-color-scheme.
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  return (
    <>
      <button
        type="button"
        aria-label="Signal icon"
        onClick={() => {
          setCustomText(emoji);
          setOpen(true);
        }}
        className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 border-2 ${isMultiCharSignalIcon(emoji) ? 'text-sm font-semibold' : 'text-lg'}`}
        style={{ background: `${colorHex}1f`, borderColor: colorHex }}
      >
        {emoji || '🙂'}
      </button>
      <Sheet open={open} onClose={() => setOpen(false)} title="Choose icon">
        <form
          className="flex items-center gap-2 mb-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!customText) return;
            onEmojiChange(customText);
            setOpen(false);
          }}
        >
          <TextField
            aria-label="Custom icon text"
            placeholder="Type up to 2 characters, e.g. AB or 42"
            value={customText}
            onChange={(e) => setCustomText(e.target.value.slice(0, 2))}
            maxLength={2}
            className="flex-1"
          />
          <Button type="submit" disabled={!customText}>
            Use
          </Button>
        </form>
        <Suspense
          fallback={<div className="h-[360px] flex items-center justify-center text-sm text-black/40 dark:text-white/40">Loading…</div>}
        >
          <EmojiPicker
            autoFocusSearch
            searchPlaceHolder="Search emoji"
            emojiStyle={'native' as EmojiStyle}
            theme={(isDark ? 'dark' : 'light') as Theme}
            width="100%"
            height={360}
            onEmojiClick={(data) => {
              onEmojiChange(data.emoji);
              setOpen(false);
            }}
          />
        </Suspense>
      </Sheet>
    </>
  );
}
